import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { ISystem } from '../../ts/interface/system.interface';
import { IUserDetail } from '../../ts/interface/userDetail.interface';
import { useSystemStore } from '../../store/systemStore';
import {
  useUsersRelayQuery,
  mapRelayUsersToIUserDetails,
  UserSortInput,
  UserQueryFilter,
} from '../../hooks/users/query/V2';
import { history } from '../../utils';
import {
  IColumn,
  ISortConfig,
} from '../../ts/interface/components/redesing/table.interface';
import PageContainer from '../../components/Redesing/page-container';
import { Typography } from '@mui/material';
import Card from '@mui/material/Card';
import { PaginationTable } from '../../components/Redesing/table/pagination-table';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import SendIcon from '@mui/icons-material/Send';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useDirectusData from '../../hooks/directus/query/useDirectusData';
import { DirectusData } from '../../ts/interface/directus';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants';
import useUrlState from '../../hooks/use-url-state';
import {
  MovaticFieldGroupSelectFilter,
  MovaticFieldGroupText,
} from '../../components/Redesing/movatic-field-group';
import { SearchChip, Option } from '../../ts/interface/components/table.interface';
import MovaticCustomModal from '../../components/Modal/MovaticCustomModal';
import IconButtonMenu from '../../components/Redesing/icon-button-menu';
import { sendUserNotifications } from '../../actionCreators/system';
import CustomAsyncToast from '../../components/Redesing/custom-async-toast';
import { userColumns } from '../../tableColumns/userColumns';
import useSearchDebounce from '../../hooks/search/useSearchDebounce';
import { useKeysetPagination } from '../../hooks/use-keyset-pagination';

const filterOptions = [{ value: 'non-hidden', label: 'Non-Hidden' }];

const sortFieldMap: Record<string, string> = {
  fullName: 'FULL_NAME',
  joined: 'JOINED',
};

const Index = ({ system, systemLoaded }: { system: ISystem; systemLoaded: boolean }) => {
  const [exportData, setExportData] = useState<boolean>(false);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationDescription, setNotificationDescription] = useState('');

  // Shared server-side keyset pagination (page# kept local, not in the URL).
  const {
    pageNumber,
    after,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset: resetPagination,
  } = useKeysetPagination();

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    rowsPerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    // Applied Status filter (drives the query and the chips). Defaults to
    // "non-hidden" (which maps to hidden=false server-side).
    status: 'non-hidden',
    // Pending selection for the Status dropdown (not yet applied).
    selectedStatus: [{ label: 'Non-Hidden', value: 'non-hidden' }] as Option[],
    sort: {
      key: 'fullName',
      direction: 'asc',
    } as ISortConfig,
  });

  const debouncedSearchTerm = useSearchDebounce(filters.search);

  const { data: directus } = useDirectusData(
    'adminSiteSpace',
    { code: 'users' },
    true
  ) as {
    data: { value: DirectusData } | undefined;
  };
  const directusData = directus?.value;

  // Get system ID from Zustand store for GraphQL filtering
  const { systemInfo } = useSystemStore();
  const systemGid = systemInfo.gid;

  // Build server-side GraphQL filter from the applied, URL-persisted values.
  const userFilter = useMemo((): UserQueryFilter | undefined => {
    if (!systemGid) return undefined;
    const filter: UserQueryFilter = { systemId: systemGid };
    if (debouncedSearchTerm) {
      // Server-side: matches the name, plus the phone number when the term is
      // all digits (see UserQueryFilter.search).
      filter.search = debouncedSearchTerm;
    }
    if (filters.status === 'non-hidden') {
      filter.hidden = false;
    }
    return filter;
  }, [systemGid, debouncedSearchTerm, filters.status]);

  // Build GraphQL sort from sort state
  const graphqlSort: UserSortInput[] = useMemo(() => {
    const field = sortFieldMap[filters.sort.key];
    if (!field) return [{ field: 'CREATED_ON' as const, order: 'DESC' as const }];
    return [
      {
        field: field as UserSortInput['field'],
        order: (filters.sort.direction === 'asc' ? 'ASC' : 'DESC') as 'ASC' | 'DESC',
      },
    ];
  }, [filters.sort]);

  // Use GraphQL Relay query with server-side pagination
  const {
    data: graphqlData,
    count: totalUsers,
    hasMore: graphqlHasMore,
    endCursor,
    isLoading: isLoadingUsers,
    isRefetching,
  } = useUsersRelayQuery(userFilter, {
    limit: filters.rowsPerPage,
    after,
    sort: graphqlSort,
    enabled: systemLoaded && isComplete && Boolean(systemGid),
  });

  // Only the current page's rows are shown (server-side pagination, no
  // client-side accumulation).
  const usersItems = useMemo(
    () =>
      (graphqlData ?? []).map(
        (user) => mapRelayUsersToIUserDetails([user], systemGid)[0]
      ),
    [graphqlData, systemGid]
  );

  // Record the next page's cursor and clear the paginating loader when a fetch
  // settles.
  useEffect(() => {
    onPageLoaded(endCursor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphqlData]);

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Rebuild the chip list from the applied, URL-persisted Status filter so the
  // chips always match the fetched data — after a refresh and when cleared.
  useEffect(() => {
    if (isRestoring) return;
    const next: SearchChip[] = [];
    if (filters.status) {
      const option = filterOptions.find((o) => o.value === filters.status);
      next.push({
        label: 'Configuration',
        field: 'configuration',
        value: filters.status,
        displayValue: option?.label ?? filters.status,
        disabled: false,
      });
    }
    setChips(next);
  }, [isRestoring, filters.status]);

  const columns = useMemo(() => {
    const updatedColumns = [...userColumns];

    if (system.privateSystem) {
      const hasMembershipColumn = updatedColumns.find(
        (column) => column.id === 'has_membership'
      );
      const bannedColumn = updatedColumns.find((column) => column.id === 'banned');

      if (!hasMembershipColumn) {
        updatedColumns.push({
          id: 'has_membership',
          label: 'Membership',
          notSortable: true,
          exportFormat: (cell: IUserDetail) => (cell.has_membership ? 'Yes' : 'No'),
          format: (cell: IUserDetail) => {
            const hasMembership = cell?.has_membership;
            return (
              <SeverityPill color={hasMembership ? 'success' : 'error'}>
                {hasMembership ? 'Active' : 'None'}
              </SeverityPill>
            );
          },
        });
      }

      if (!bannedColumn) {
        updatedColumns.push({
          id: 'banned',
          label: 'Status',
          notSortable: true,
          exportFormat: (cell: IUserDetail) => (cell.banned ? 'Banned' : 'Active'),
          format: (cell: IUserDetail) => {
            const banned = cell?.banned;
            return (
              <SeverityPill color={banned ? 'error' : 'success'}>
                {banned ? 'Banned' : 'Active'}
              </SeverityPill>
            );
          },
        });
      }
    }

    return updatedColumns;
  }, [system.privateSystem]);

  const handleChipDelete = useCallback(
    (deletedGroup: SearchChip) => {
      resetPagination();
      setChips((prevChips) =>
        prevChips.filter((group) => group.label !== deletedGroup.label)
      );

      if (deletedGroup.label === 'Configuration') {
        setFilters((prevState) => ({
          ...prevState,
          status: '',
          selectedStatus: [],
        }));
      }
    },
    [setChips, setFilters, resetPagination]
  );

  const pageButtons = [
    {
      label: 'Export',
      onClick: () => setExportData(true),
      startIcon: <FileDownloadIcon />,
    },
    {
      startIcon: <AddIcon />,
      label: 'Add',
      onClick: () => history.push('/users/add'),
    },
    {
      label: 'Add CSV',
      onClick: () => history.push('/users/add_csv'),
      startIcon: <FileUploadIcon />,
    },
    ...(system.partner !== 0
      ? [
          {
            label: 'Send notifications',
            onClick: () => setOpenFilterModal(true),
            startIcon: <SendIcon />,
          },
        ]
      : []),
  ];

  return (
    <PageContainer
      csvReport={{
        filename: 'Users.csv',
        // NOTE: export currently reflects only the current page's rows
        // (server-side pagination fetches one page at a time).
        data: usersItems || [],
        headers: [
          'fullName',
          'emailAddress',
          'phone',
          'joined',
          'has_membership',
          'banned',
        ],
      }}
      onImportClick={() => history.push('/users/add_csv')}
      importLabel={'Add CSV'}
      spaceName={'Users'}
      documentationLink={directusData?.documentation_link}
    >
      <Card>
        <PaginationTable
          totalItems={totalUsers}
          chips={chips}
          handleChipDelete={handleChipDelete}
          hasMore={graphqlHasMore}
          isLoading={isLoadingUsers || !systemLoaded || !isComplete || isPaginating}
          isLoadingCache={isRefetching}
          onApplyFilter={() => {
            const nextStatus = filters.selectedStatus?.[0]?.value ?? '';
            if (nextStatus !== filters.status) {
              resetPagination();
              setFilters((prevState) => ({
                ...prevState,
                status: nextStatus,
              }));
            }
          }}
          spaceFilters={
            <>
              <MovaticFieldGroupSelectFilter
                label={'Status'}
                value={filters.selectedStatus}
                onChange={(event: React.SyntheticEvent, newValue: any[]) => {
                  setFilters((prevState) => ({
                    ...prevState,
                    selectedStatus: newValue
                      .filter((option) =>
                        filterOptions.some(
                          (item) => item.value === (option.value ?? option)
                        )
                      )
                      .map((option) => {
                        const value = option.value ?? option;
                        return {
                          label: filterOptions.find((item) => item.value === value)!
                            .label,
                          value,
                        };
                      }),
                  }));
                }}
                options={filterOptions}
              />
            </>
          }
          exportData={exportData}
          csvFileName={'Users.csv'}
          onExport={() => setExportData(false)}
          buttons={pageButtons}
          padding={{
            pl: -1,
            pt: 1,
            pr: 1,
          }}
          onRowCLick={(id: string) => history.push(`/users/${id}/general`)}
          localSearch={false}
          noDataSlice
          customKey={'phone'}
          handleSearch={(e: React.ChangeEvent<HTMLInputElement>) => {
            resetPagination();
            setFilters((prevState) => ({ ...prevState, search: e.target.value }));
          }}
          onSortChange={(sort) => {
            resetPagination();
            setFilters((prevState) => ({
              ...prevState,
              sort: {
                key: sort.key,
                direction: sort.direction as 'asc' | 'desc',
              },
            }));
          }}
          sortBy={filters.sort.key}
          sortDirection={filters.sort.direction}
          searchText={filters.search}
          columns={columns as IColumn[]}
          items={usersItems || []}
          onPageChange={(page: any, pageN: number) => onPageChange(pageN)}
          onRowsPerPageChange={(row: any) => {
            resetPagination();
            setFilters((prevState) => ({
              ...prevState,
              rowsPerPage: Number(row.target.value),
            }));
          }}
          page={pageNumber}
          rowsPerPage={filters.rowsPerPage}
          searchPlaceholder={'Search users'}
        />
      </Card>
      <MovaticCustomModal
        customStyles={{
          title: {
            p: 2,
          },
          action: {
            p: -2,
          },
          content: {
            p: 2,
          },
        }}
        open={openFilterModal}
        onClose={() => setOpenFilterModal(false)}
        content={
          <div>
            <Typography variant="body1">
              This action will send notifications to all users
            </Typography>
            <MovaticFieldGroupText
              id="chargePendingDescriptionGroup"
              label="Title (Required)"
              value={notificationTitle || ''}
              onChange={(e) => setNotificationTitle(e.target.value)}
              description="The name of the system will be prepended to this title."
            />
            <MovaticFieldGroupText
              multiline
              rows={2}
              id="chargePendingDescriptionGroup"
              label="Description (Required)"
              value={notificationDescription || ''}
              onChange={(e) => setNotificationDescription(e.target.value)}
            />
          </div>
        }
        title={'Send notifications'}
        action={
          <IconButtonMenu
            buttons={[
              {
                label: 'Cancel',
                onClick: () => setOpenFilterModal(false),
              },
              {
                label: 'Send',
                onClick: () => {
                  CustomAsyncToast({
                    promise: () =>
                      sendUserNotifications(notificationTitle, notificationDescription),
                    successMessage: () => 'The notifications were sent successfully',
                    loadingMessage: 'Sending notifications...',
                    errorMessage: 'There was an error sending the notifications',
                  }).then(() => {
                    setOpenFilterModal(false);
                    setNotificationTitle('');
                    setNotificationDescription('');
                  });
                },
                disabled: !notificationTitle || !notificationDescription,
              },
            ]}
          />
        }
      />
    </PageContainer>
  );
};

const UsersR = connect(
  (state: { system: { current: ISystem; isLoaded: boolean } }) => ({
    system: state.system.current,
    systemLoaded: state.system.isLoaded,
  }),
  () => ({})
)(Index);

export default UsersR;
